// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import React, { type FC, useEffect, useMemo } from "react";

import {
  BooleanIcon,
  Box,
  Column,
  EditIcon,
  ExternalLinkIcon,
  GroupedCombobox,
  MeetingIcon,
  NumberIcon,
  QuestionIcon,
  Row,
  Select,
  Text,
  TextInput,
  TextStringIcon,
  TimeIcon,
  TraitIcon,
  WarningIcon,
} from "@hightouch/htui";
import capitalize from "lodash/capitalize";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isPresent } from "ts-extras";
import { v4 as uuidv4 } from "uuid";

import { useFormErrorContext } from "src/contexts/form-error-context";
import { useFilterableColumnsQuery } from "src/graphql";
import {
  isRelatedColumn,
  DefaultOperators,
  isColumnReference,
  type PropertyCondition,
  isTraitCondition,
  isInlineAggregatedTraitCondition,
  isJourneyEventColumn,
  type ColumnReference,
  isRelatedJourneyEventColumn,
  isInlineTraitColumn,
  isFormulaTraitCondition,
  isMergedColumn,
  PropertyComparisonType,
  ColumnType,
  TraitType,
} from "src/types/visual";

import { FilterPopover } from "src/components/explore/filter-popover";
import { getPropertyLabel } from "src/components/explore/filter-popover/utils";
import { ARRAY_ITEM_SEPARATOR } from "src/pages/schema/columns/object-array-columns";
import type { FilterProps } from "./condition";
import { HStack } from "./shared";
import { DetailButton, XButton } from "./condition-buttons";
import { ConditionTextWrapper } from "./condition-text-wrapper";
import {
  validatePropertyCondition,
  isRealtimeEntryEventColumnTypeSupported,
  type ValidationTraitDefinition,
} from "./condition-validation";
import { ErrorMessage } from "./error-message";
import {
  FilterActionsMenu,
  makeAddNestedGroupMenuItem,
} from "./filter-actions-menu";
import { InlineTrait } from "./inline-trait";
import { TraitInlineSubconditions } from "./trait-filter";
import {
  buildFilterableColumnIndex,
  getModelIdFromColumn,
  getModelIdFromTrait,
  getOperatorLabel,
  getPropertyConditionTypeDrift,
  getPropertyNameFromColumn,
  getPropertyNameFromTrait,
} from "./utils";
import { useFetchSuggestions } from "src/hooks/use-column-suggestions";
import { isCepControlPlaneProperty } from "src/cep/utils";
import {
  ConditionType,
  getPropertyType,
  isJsonPathColumn,
  JsonOperator,
  type TraitCondition,
} from "@hightouch/lib/query/visual/types";
import { PropertyFilterJsonSubconditions } from "./property-filter-json-subconditions";
import { ObjectArrayItemFilters } from "./object-array-item-filters";
import {
  getPropertyValueFilter,
  isCatalogRecommendationTrait,
} from "./property-value-filter";
import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import { Link } from "src/router";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";

const EMPTY_ARRAY = [];

export type PropertyFilterProps = FilterProps<PropertyCondition> & {
  prefix?: boolean;
  /**
   * For standard property filters, the LHS property filter popover lives outside of the PropertyFilter component.
   * For some versions like event subconditions, the PropertyFilter itself controls the LHS property configuration.
   * For json subconditions, users can directly enter text to configure the LHS property.
   */
  lhsPropertyFilterVariant?: "dropdown" | "text";
  showParameterizeCheckbox?: boolean;
  propertyComparisonType?: PropertyComparisonType;
  includeParentInPropertyComparison?: boolean;
  isNested?: boolean;
  isWrappedWithAndCondition?: boolean;
  showEventPropertyTypeDriftWarning?: boolean;
};

export const usePropertyFilterValidation = (
  condition: PropertyCondition,
  opts?: {
    isRealtimeAudience: boolean;
    isRealtimeJourneyCriteria?: boolean;
    isRealtimeEntryEventArrayItem?: boolean;
    traitsById?: ReadonlyMap<string, ValidationTraitDefinition>;
  },
) => {
  const filterId = useMemo<string>(uuidv4, []);
  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();
  const {
    realtimeTraitsInRtaConditions = false,
    rtaWorkerWarehouseTraitMerge = false,
  } = useFlags();

  const filterErrors = getErrors(filterId, { allErrors: true });
  const conditionError = filterErrors?.condition;
  const propertyError = filterErrors?.property;
  const operatorError = filterErrors?.operator;
  const valueError = filterErrors?.value;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(
      filterId,
      validatePropertyCondition(condition, {
        isRealtimeAudience: opts?.isRealtimeAudience,
        isRealtimeJourneyCriteria: opts?.isRealtimeJourneyCriteria,
        isRealtimeEntryEventArrayItem: opts?.isRealtimeEntryEventArrayItem,
        traitsById: opts?.traitsById,
        realtimeTraitsInRtaConditionsEnabled: realtimeTraitsInRtaConditions,
        rtaWorkerWarehouseTraitMergeEnabled: rtaWorkerWarehouseTraitMerge,
      }),
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [
    condition.property,
    condition.operator,
    condition.value,
    filterId,
    condition.propertyOptions,
    condition.subconditions,
    opts?.isRealtimeAudience,
    opts?.isRealtimeJourneyCriteria,
    opts?.isRealtimeEntryEventArrayItem,
    opts?.traitsById,
    realtimeTraitsInRtaConditions,
    rtaWorkerWarehouseTraitMerge,
  ]);

  return {
    conditionError,
    filterErrors,
    propertyError,
    operatorError,
    valueError,
  };
};

const castTypes = [
  [ColumnType.String, TextStringIcon],
  [ColumnType.Number, NumberIcon],
  [ColumnType.Timestamp, TimeIcon],
  [ColumnType.Date, MeetingIcon],
  [ColumnType.Boolean, BooleanIcon],
  [ColumnType.Unknown, QuestionIcon],
  // BigInt not supported, should be able to use string instead
] as const;

const castTypeOptions = castTypes.map(([key, icon]) => {
  return {
    label: capitalize(key),
    value: key,
    icon,
  };
});

export const PropertyFilter: FC<Readonly<PropertyFilterProps>> = React.memo(
  function PropertyFilterUnmemoized({
    lhsPropertyFilterVariant,
    showParameterizeCheckbox = false,
    isNested = false,
    propertyComparisonType = PropertyComparisonType.Default,
    includeParentInPropertyComparison,
    isWrappedWithAndCondition,
    showEventPropertyTypeDriftWarning = false,
    onGroup,
    level,
    ...props
  }) {
    const { condition, onUpdate, onRemove, audience, parent } = props;

    const {
      realtimeConditionScope,
      maxNestingDepth,
      journeyTraits,
      rootModelId,
    } = useQueryBuilderContext();
    const isRealtimeAudience = realtimeConditionScope === "audience";
    const isRealtimeJourneyCriteria =
      realtimeConditionScope === "journey-criteria";
    const { columns, traits, traitsById, isCepManagedModel } =
      useFilterableOptionsContext();

    const disableGrouping = (level ?? 0) >= maxNestingDepth;

    const { conditionError, propertyError, operatorError, valueError } =
      usePropertyFilterValidation(condition, {
        isRealtimeAudience,
        isRealtimeJourneyCriteria,
        traitsById,
      });

    const isTrait = isTraitCondition(condition);
    const isFormulaTrait = isFormulaTraitCondition(condition);

    const traitId =
      isTrait || isFormulaTrait
        ? condition.property.column.traitDefinitionId
        : undefined;

    const eventModelId = isRelatedJourneyEventColumn(condition.property)
      ? condition.property.column.modelId
      : null;
    const journeyEventColumnConditionSelected = Boolean(eventModelId);

    // TODO(nolan): this is for the special journey entry event
    // filter RHS. Can we move this into a separate PropertyOperatorValue component?
    // Fetch columns for the selected event, if there is one
    const filterableColumnsQuery = useFilterableColumnsQuery(
      {
        segmentId: eventModelId?.toString() ?? "",
      },
      {
        // Only used for journey event columns
        enabled:
          Boolean(eventModelId) &&
          isRelatedJourneyEventColumn(condition.property),
        select: (data) =>
          data.segments_by_pk?.filterable_audience_columns ?? [],
      },
    );
    const eventColumns = filterableColumnsQuery.data ?? [];
    // Item fields of an array of objects are filtered under the array's any/all condition, not picked on their own.
    const topLevelEventColumns = useMemo(
      () =>
        (filterableColumnsQuery.data ?? []).filter(
          (column) =>
            !column.name.includes(ARRAY_ITEM_SEPARATOR) &&
            !isMergedColumn(column.column_reference) &&
            (!isRealtimeJourneyCriteria ||
              isRealtimeEntryEventColumnTypeSupported(column.type)),
        ),
      [filterableColumnsQuery.data, isRealtimeJourneyCriteria],
    );

    const trait = isTrait
      ? traits.find(
          ({ id }) => id === condition.property.column.traitDefinitionId,
        )
      : null;

    const isCatalogRecTrait = isCatalogRecommendationTrait(trait);
    // Catalog recommendation traits take per-audience item filters inline, like trait templates
    const showReadOnlySubconditions =
      isTrait &&
      condition.propertyOptions?.traitType === "trait" &&
      !isCatalogRecTrait;

    let traitSubconditions: PropertyCondition[] | TraitCondition[] =
      EMPTY_ARRAY;
    // Trait template inline subconditions are editable and handled elsewhere
    // Only direct trait subconditions are rendered readonly
    if (showReadOnlySubconditions) {
      if (
        condition.property.column.conditions &&
        condition.property.column.conditions.length > 0
      ) {
        traitSubconditions = condition.property.column.conditions;
      } else if (
        trait?.config &&
        "conditions" in trait.config &&
        Array.isArray(trait?.config.conditions) &&
        trait.config.conditions.length > 0
      ) {
        traitSubconditions = trait.config.conditions;
      }
    }

    const relationships = parent?.relationships ?? [];

    const modelId = isColumnReference(condition.property)
      ? isTrait
        ? getModelIdFromTrait(trait)
        : isRelatedColumn(condition.property) &&
            isInlineTraitColumn(condition.property.column)
          ? relationships.find(
              (relationship) =>
                isRelatedColumn(condition.property) &&
                isInlineTraitColumn(condition.property.column) &&
                relationship.id.toString() ===
                  condition.property.column?.relationshipId.toString(),
            )?.to_model.id
          : getModelIdFromColumn(condition.property)
      : parent?.id;

    const columnName = isColumnReference(condition.property)
      ? isTrait
        ? getPropertyNameFromTrait(trait)
        : getPropertyNameFromColumn(condition.property)
      : condition.property;

    const isControlPlaneProperty = isCepControlPlaneProperty({
      isCepManagedModel,
      columnName,
    });

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    const shouldFetchSuggestions = useMemo(() => {
      if (isControlPlaneProperty) return false;
      if (isTrait && isPresent(trait?.id)) return true;
      return isPresent(modelId) && isPresent(columnName);
    }, [modelId, columnName, isTrait, isControlPlaneProperty]);
    const {
      suggestions,
      loadingSuggestions,
      error: suggestionsError,
      onSearchUpdate,
    } = useFetchSuggestions(
      {
        modelId,
        columnName: columnName ?? undefined,
        traitId,
        // The audience's root parent model, from context rather than
        // `parent`: the related-model and event surfaces reassign `parent`
        // to the model the subcondition filters on, which is not a parent
        // model and has no space-scoped suggestions of its own.
        parentModelId: rootModelId ?? undefined,
      },
      { enabled: shouldFetchSuggestions },
    );

    const convertToInlineTrait = () => {
      if (!trait || !trait.type) return;

      const traitType = trait.type as TraitType;
      if (
        traitType === TraitType.Formula ||
        traitType === TraitType.RealtimeFormula
      ) {
        return;
      }

      onUpdate((condition) => {
        if (
          isColumnReference(condition.property) &&
          "path" in condition.property &&
          Array.isArray(condition.property.path)
        ) {
          const { conditions, ...traitConfig } = trait?.config ?? {};
          condition.property = {
            type: "related",
            path: condition.property.path,
            column: {
              type: "inline_trait",
              traitType,
              traitConfig,
              conditions: conditions ?? [],
              relationshipId: trait?.relationship?.id,
            },
          };
          if (!condition.propertyOptions) {
            condition.propertyOptions = {};
          }
          condition.propertyOptions.traitType = "inline_trait";
        }
      });
    };

    const PropertyValueFilter = getPropertyValueFilter({
      isControlPlaneProperty,
      trait,
      propertyComparisonType,
    });

    // Capture condition property for type narrowing in closures
    const conditionProperty = condition.property;

    const menuItems = useMemo(
      () =>
        [
          onGroup
            ? makeAddNestedGroupMenuItem({
                currentConditionType: isWrappedWithAndCondition
                  ? ConditionType.And
                  : ConditionType.Or,
                onGroup,
                isDisabled: disableGrouping,
              })
            : undefined,
        ].filter(isPresent),
      [onGroup, isWrappedWithAndCondition, disableGrouping],
    );

    // Size the operator + value pill to fit the operator label so it never clips
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    const operatorMinWidth = useMemo(() => {
      const label = getOperatorLabel(
        condition.operator,
        getPropertyType(condition),
        {
          parameterized: Boolean(condition.propertyOptions?.parameterize),
        },
      );
      return label ? `${label.length + 4}ch` : "80px";
    }, [condition.operator, condition.property, condition.propertyOptions]);

    const filterableColumnIndex = useMemo(
      () =>
        showEventPropertyTypeDriftWarning
          ? buildFilterableColumnIndex(columns)
          : null,
      [columns, showEventPropertyTypeDriftWarning],
    );

    const typeDrift = useMemo(
      () =>
        showEventPropertyTypeDriftWarning && filterableColumnIndex
          ? getPropertyConditionTypeDrift(condition, filterableColumnIndex)
          : null,
      [condition, filterableColumnIndex, showEventPropertyTypeDriftWarning],
    );

    // When a column has a custom_type override, condition.propertyType may be
    // stale (set at creation time from the raw type). Silently correct it so
    // operator options, value/property/trait toggle, and RHS column picker all
    // behave as if the column was freshly selected.
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (!isColumnReference(condition.property)) return;
      const name = getPropertyNameFromColumn(condition.property);
      const colModelId = getModelIdFromColumn(condition.property);
      const col = columns.find(
        (c) => c.name === name && String(c.model_id) === String(colModelId),
      );
      if (!col?.custom_type) return;
      const effectiveType = col.custom_type as ColumnType;
      if (condition.propertyType !== effectiveType) {
        onUpdate((c) => {
          c.propertyType = effectiveType;
        });
      }
    }, [columns, condition.property, condition.propertyType]);

    return (
      <Column gap={4}>
        <HStack gap={2} alignItems="flex-start" minWidth={0}>
          {/* Show filter popover at beginning of filter */}
          {journeyEventColumnConditionSelected && (
            <FilterPopover
              {...props}
              condition={condition}
              isDisabled={loadingSuggestions || props.isDisabled}
              onChange={(newCondition) => onUpdate(() => newCondition)}
            />
          )}
          {!journeyEventColumnConditionSelected &&
          lhsPropertyFilterVariant === "dropdown" ? (
            <Column>
              <FilterPopover
                {...props}
                placeholder="Select a property"
                condition={condition}
                isLoading={props.isLoading}
                isDisabled={props.isDisabled}
                hasError={Boolean(propertyError)}
                onChange={(newCondition) => onUpdate(() => newCondition)}
              />
              {propertyError && <ErrorMessage>{propertyError}</ErrorMessage>}
            </Column>
          ) : !journeyEventColumnConditionSelected &&
            lhsPropertyFilterVariant === "text" &&
            isJsonPathColumn(conditionProperty) ? (
            <Column>
              <Row gap={2}>
                <Column>
                  <TextInput
                    width="xs"
                    placeholder="Enter a JSON key"
                    value={conditionProperty.path ?? ""}
                    isInvalid={Boolean(propertyError)}
                    onChange={(e) =>
                      onUpdate((condition) => {
                        if (
                          condition.property &&
                          typeof condition.property === "object" &&
                          "path" in condition.property
                        ) {
                          condition.property.path = e.target.value;
                        }
                      })
                    }
                  />
                  {propertyError && (
                    <ErrorMessage>{propertyError}</ErrorMessage>
                  )}
                </Column>
                <Select
                  isDisabled={props.isDisabled}
                  options={castTypeOptions}
                  width="auto"
                  value={conditionProperty.castType ?? ""}
                  optionAccessories={(option) => ({
                    leftAccessory: {
                      type: "icon",
                      icon: option.icon,
                    },
                  })}
                  // HTUI doesn't support null value labels yet. Empty string mostly works, may remove aria label and add an extra space.
                  valueLabel={() => ""}
                  onChange={(value) => {
                    if (value && value !== conditionProperty.castType) {
                      onUpdate((condition) => {
                        if (isJsonPathColumn(condition.property)) {
                          condition.property.castType = value as ColumnType;
                          condition.value = null;
                          condition.operator =
                            DefaultOperators[value as ColumnType] ?? null;
                        }
                      });
                    }
                  }}
                />
              </Row>
            </Column>
          ) : (
            <>
              {condition?.propertyOptions?.traitType === "inline_trait" ? (
                <>
                  {isRelatedColumn(condition?.property) &&
                    !isJourneyEventColumn(condition.property.column) && (
                      <Column>
                        <ConditionTextWrapper>
                          <Text color="text.secondary">Have</Text>
                          <Box
                            as={TraitIcon}
                            bg="white"
                            color="warning.border"
                            borderRadius="sm"
                            borderColor="warning.border"
                            borderWidth={1}
                            height={5}
                            width={5}
                            p={0.5}
                          />
                          <Text color="text.secondary" fontWeight="medium">
                            Trait calculation
                          </Text>
                        </ConditionTextWrapper>

                        {parent &&
                          isRelatedColumn(condition.property) &&
                          isInlineAggregatedTraitCondition(condition) && (
                            <InlineTrait
                              modelId={modelId}
                              isLoading={props.isLoading}
                              isDisabled={props.isDisabled}
                              audience={audience}
                              condition={condition}
                              conditionError={conditionError}
                              loadingSuggestions={loadingSuggestions}
                              operatorError={operatorError}
                              parent={parent}
                              propertyError={propertyError}
                              suggestions={suggestions}
                              valueError={valueError}
                              onUpdate={onUpdate as any}
                              onSearchUpdate={onSearchUpdate}
                            />
                          )}
                      </Column>
                    )}
                </>
              ) : (
                <>
                  <ConditionTextWrapper>
                    <Text color="text.secondary">
                      {isTrait
                        ? "Have custom trait"
                        : journeyEventColumnConditionSelected
                          ? "have property"
                          : "Have property"}
                    </Text>
                  </ConditionTextWrapper>
                  {/* Show here if not an event property */}
                  {!journeyEventColumnConditionSelected && (
                    <FilterPopover
                      {...props}
                      condition={condition}
                      isDisabled={loadingSuggestions || props.isDisabled}
                      hasError={Boolean(propertyError)}
                      onChange={(newCondition) => onUpdate(() => newCondition)}
                    />
                  )}
                  {isRelatedJourneyEventColumn(condition.property) && (
                    <Column>
                      <GroupedCombobox
                        isDisabled={props.isDisabled}
                        isInvalid={Boolean(propertyError)}
                        isLoading={
                          props.isLoading || filterableColumnsQuery.isLoading
                        }
                        optionGroups={[
                          {
                            label: "Event properties",
                            options: topLevelEventColumns,
                          },
                        ]}
                        optionLabel={(option) =>
                          getPropertyLabel(option.alias, option.name)
                        }
                        optionDescription={(option) => option.description || ""}
                        optionValue={(option) => option}
                        placeholder="Select a property"
                        popoverWidth={isNested ? "xs" : undefined}
                        width="4xs"
                        value={topLevelEventColumns.find(
                          (column) =>
                            isRelatedJourneyEventColumn(condition.property) &&
                            getPropertyNameFromColumn(
                              column.column_reference,
                            ) === condition.property.column.name,
                        )}
                        onChange={(column) => {
                          // column.column_reference is `any` type
                          if (column) {
                            const columnReference =
                              column?.column_reference as ColumnReference;

                            const columnName =
                              getPropertyNameFromColumn(columnReference);

                            onUpdate((condition) => {
                              // This really needs a custom builder that guarantees a valid journey event condition is formed
                              if (
                                condition.property &&
                                typeof condition.property === "object" &&
                                "column" in condition.property &&
                                columnName &&
                                eventModelId
                              ) {
                                condition.property.column = {
                                  name: columnName,
                                  modelId: eventModelId,
                                  type: "journey_event",
                                };
                              }

                              const columnType = column.type as ColumnType;
                              condition.propertyType = columnType;
                              condition.operator = columnType
                                ? DefaultOperators[columnType]
                                : null;
                              condition.value = null;
                              delete condition.subconditions;
                            });
                          }
                        }}
                      />
                      {propertyError && (
                        <ErrorMessage>{propertyError}</ErrorMessage>
                      )}
                    </Column>
                  )}
                </>
              )}
            </>
          )}

          {condition.property &&
            condition?.propertyOptions?.traitType !== "inline_trait" && (
              <Box
                minWidth={operatorMinWidth}
                // By default, HStack applies flexShrink: 1 to all children*, we need to bypass that
                // so the Box can shrink to fit the content on smaller viewports
                flexShrink="1 !important"
              >
                <PropertyValueFilter
                  isDisabled={props.isDisabled}
                  allowPropertyComparison={
                    propertyComparisonType !== PropertyComparisonType.None &&
                    !isRealtimeJourneyCriteria
                  }
                  includeParentInPropertyComparison={
                    includeParentInPropertyComparison
                  }
                  condition={condition}
                  loadingSuggestions={loadingSuggestions}
                  operatorError={operatorError}
                  parent={parent}
                  showParameterizeCheckbox={showParameterizeCheckbox}
                  suggestions={suggestions}
                  suggestionsError={suggestionsError}
                  valueError={valueError}
                  onUpdate={onUpdate}
                  onSearchUpdate={onSearchUpdate}
                  columns={
                    journeyEventColumnConditionSelected ? eventColumns : columns
                  }
                />
              </Box>
            )}

          {isTrait && (
            <Link
              isExternal
              href={
                // The traits list filters out journey traits; open those in
                // the journey's own trait drawer instead.
                journeyTraits?.some(({ id }) => id === trait?.id)
                  ? `?view=trait&traitId=${trait?.id ?? ""}`
                  : `/traits/active/${trait?.id ?? ""}`
              }
              alignItems="center"
              display="flex"
              fontSize="sm"
              lineHeight="8"
            >
              Trait definition
              <ExternalLinkIcon ml={1} />
            </Link>
          )}

          {onRemove && (
            <XButton isDisabled={props.isDisabled} onRemove={onRemove} />
          )}

          {!props.isDisabled && <FilterActionsMenu items={menuItems} />}
        </HStack>
        {typeDrift && (
          <PropertyTypeDriftWarning
            storedType={typeDrift.storedType}
            liveType={typeDrift.liveType}
          />
        )}
        {getPropertyType(condition) === ColumnType.JsonArrayObjects && (
          <ObjectArrayItemFilters
            columns={
              journeyEventColumnConditionSelected ? eventColumns : undefined
            }
            condition={condition}
            isDisabled={props.isDisabled}
            parent={parent}
            onUpdate={onUpdate}
          />
        )}
        {getPropertyType(condition) === ColumnType.Json &&
          lhsPropertyFilterVariant !== "text" &&
          condition.operator === JsonOperator.Exists && (
            <PropertyFilterJsonSubconditions
              {...props}
              pl={lhsPropertyFilterVariant === "dropdown" ? 0 : 10}
              isRealtimeAudience={isRealtimeAudience}
            />
          )}
        {isTrait && (
          <Column pl={10} gap={2}>
            {showReadOnlySubconditions ? (
              <>
                {traitSubconditions.length > 0 && (
                  <Text color="text.secondary">Where</Text>
                )}
                <ReadOnlyConditions
                  audiences={EMPTY_ARRAY}
                  relationships={EMPTY_ARRAY}
                  traits={EMPTY_ARRAY}
                  journeys={EMPTY_ARRAY}
                  rootConditions={traitSubconditions}
                  noBackgroundColor
                  hideIcons
                />
                {!isNested && trait?.type !== TraitType.Predictive && (
                  <DetailButton
                    icon={EditIcon}
                    size="sm"
                    isDisabled={props.isDisabled}
                    onClick={convertToInlineTrait}
                  >
                    Detach and edit
                  </DetailButton>
                )}
              </>
            ) : (
              <TraitInlineSubconditions
                {...props}
                // @ts-expect-error issue with different trait types
                onUpdate={onUpdate}
                isNested={isNested}
                condition={condition}
              />
            )}
          </Column>
        )}
      </Column>
    );
  },
);

const COLUMN_TYPES_WITH_ARTICLE = new Set<ColumnType>([
  ColumnType.String,
  ColumnType.Number,
  ColumnType.Boolean,
  ColumnType.Timestamp,
  ColumnType.Date,
  ColumnType.BigInt,
]);

const formatColumnTypeForMessage = (type: ColumnType): string =>
  COLUMN_TYPES_WITH_ARTICLE.has(type) ? `a ${type}` : type;

const PropertyTypeDriftWarning: FC<
  Readonly<{ storedType: ColumnType; liveType: ColumnType }>
> = ({ storedType, liveType }) => (
  <Row align="center" gap={2}>
    <Box as={WarningIcon} color="warning.base" fontSize="20px" flexShrink={0} />
    <Text size="sm" color="warning.base" fontWeight="medium">
      {`Type mismatch. The event property is now ${formatColumnTypeForMessage(liveType)} in your data, but this condition still uses ${formatColumnTypeForMessage(storedType)}. Please recreate the condition.`}
    </Text>
  </Row>
);
